import React, {useContext, useEffect, useState} from 'react';
import {TextField, Button, Typography, Box, Grid, Card, CardContent, CardActions, InputLabel, FormControl, Select, MenuItem} from '@mui/material';
import {GlobalStateContext} from "../state/GlobalStateProvider";
import {usePersonInformationAPI} from "../service/usePersonInformationAPI";
import {useLocation, useSearchParams} from "react-router-dom";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import Loading from "../components/common/Loading";
import AppSnackbar from "../common/AppSnackbar";
import {useTranslation} from "react-i18next";
import {useTenantConfig} from "../security/tenant-config-context";

const PersonForm = ({updateCurrentStep, providedPerson}) => {
    const {t} = useTranslation();
    const {user} = useAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const {getPersonInformation, updatePersonInformation, getPersonInformationByUuid} = usePersonInformationAPI();
    const location = useLocation();
    const {globalState} = useContext(GlobalStateContext);
    const [errors, setErrors] = useState({});
    const [personalInfo, setPersonalInfo] = useState({
        name: '', forename: '', street: '', number: '', bus: '', postalcode: '', municipality: '', country: '', gsm: '', email: '',
    });

    const countries = t("countries", {returnObjects: true});

    const [searchParams] = useSearchParams();
    const [readOnly, setReadOnly] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const {cfg} = useTenantConfig();
    const isAppointmentFlow = cfg?.businessTypes?.includes('APPOINTMENT_BASED');

    useEffect(() => {
        if (searchParams.get('uuid')) {
            setIsLoading(true)
            setReadOnly(true)
            getPersonInformationByUuid(searchParams.get('uuid'))
                .then(personInfo => setPersonalInfo(personInfo))
                .catch(() => handleFetchError(t('fetchPersonInfoError')))
                .finally(() => setIsLoading(false));
        } else if (providedPerson) {
            setPersonalInfo(providedPerson);
        } else {
            setIsLoading(true)
            getPersonInformation()
                .then(personInfo => setPersonalInfo(personInfo))
                .catch(() => handleFetchError(t('fetchPersonInfoError')))
                .finally(() => setIsLoading(false));
        }
    }, []);


    function handleFetchError(reason) {
        setSnackbarMessage(reason)
        setOpenSnackbar(true)
    }

    const validateForm = () => {
        let tempErrors = {};
        tempErrors.name = personalInfo.name ? "" : t('firstNameRequired');
        tempErrors.forename = personalInfo.forename ? "" : t('firstNameRequired');
        tempErrors.street = personalInfo.street ? "" : t('streetRequired');
        tempErrors.number = personalInfo.number ? "" : t('houseNumberRequired');
        tempErrors.postalcode = personalInfo.postalcode ? "" : t('postalCodeRequired');
        tempErrors.municipality = personalInfo.municipality ? "" : t('municipalityRequired');
        tempErrors.country = personalInfo.country ? "" : t('countryRequired');
        tempErrors.gsm = personalInfo.gsm ? "" : t('gsmRequired');
        tempErrors.email = personalInfo.email ? "" : t('emailRequired');

        if (personalInfo.email) {
            let currentEmail = personalInfo.email;
            let regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i;
            if (!regex.test(currentEmail)) {
                tempErrors.email = t('invalidEmail');
            }
        }

        setErrors({...tempErrors});
        return Object.values(tempErrors).every(x => x === "");
    };

    const handleChange = (event) => {
        const {name, value, type, checked} = event.target;
        setPersonalInfo(prevState => ({
            ...prevState, [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (validateForm()) {
            setIsLoading(true)
            updatePersonInformation(personalInfo, globalState.reservationUuid)
                .then(() => updateCurrentStep(isAppointmentFlow ? 5 : 4))
                .catch(() => handleFetchError(t('updatePersonInfoError')))
                .finally(() => setIsLoading(false));
        }
    };

    function goBack() {
        updateCurrentStep(2)
    }

    function updatePerson() {
        if (validateForm()) {
            setIsLoading(true)
            updatePersonInformation(personalInfo)
                .then(() => {
                })
                .catch(() => handleFetchError(t('updatePersonInfoError')))
                .finally(() => setIsLoading(false));
        }
    }

    return (
        <>
            {isLoading && (<Loading/>)}
            {!isLoading && (
                <Grid container alignItems="center" justifyContent="center" spacing={1} sx={{textAlign: 'center', mt: 4}}>
                    <Grid item xs={12} sm={10} md={8} lg={6}>
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h5" color="secondary">
                                        {t('personalInformation')}
                                    </Typography>
                                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{mt: 1}}>
                                        <TextField margin="normal" required fullWidth id="owner-name" label={t('name')} name="name" autoFocus value={personalInfo.name}
                                                   onChange={handleChange}
                                                   error={!!errors.name} helperText={errors.name} disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-name" }}/>
                                        <TextField margin="normal" required fullWidth id="owner-forename" label={t('firstName')} name="forename" value={personalInfo.forename}
                                                   onChange={handleChange}
                                                   error={!!errors.forename} helperText={errors.forename} disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-forename" }}/>
                                        <TextField margin="normal" required fullWidth id="contact-street" label={t('street')} name="street" value={personalInfo.street}
                                                   onChange={handleChange}
                                                   error={!!errors.street} helperText={errors.street} disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-street" }}/>
                                        <TextField margin="normal" required fullWidth id="contact-number" type="number" label={t('houseNumber')} name="number"
                                                   value={personalInfo.number}
                                                   onChange={handleChange} error={!!errors.number} helperText={errors.number} disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-number" }}/>
                                        <TextField margin="normal" fullWidth id="contact-bus" label={t('apartment')} name="bus" value={personalInfo.bus} onChange={handleChange}
                                                   disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-bus" }}/>
                                        <TextField margin="normal" required fullWidth id="contact-postalcode" label={t('postalCode')} name="postalcode"
                                                   value={personalInfo.postalcode}
                                                   onChange={handleChange} error={!!errors.postalcode} helperText={errors.postalcode} disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-postalcode" }}/>
                                        <TextField margin="normal" required fullWidth id="contact-municipality" label={t('municipality')} name="municipality"
                                                   value={personalInfo.municipality}
                                                   onChange={handleChange} error={!!errors.municipality} helperText={errors.municipality} disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-municipality" }}/>

                                        <FormControl fullWidth>
                                            <InputLabel id="country-select-label">{t('country')}</InputLabel>
                                            <Select
                                                align="left"
                                                labelId="country-select-label"
                                                id="country-select"
                                                name="country"
                                                value={personalInfo.country}
                                                onChange={handleChange}
                                                label="Country"
                                                disabled={readOnly}
                                                variant={"outlined"}
                                                inputProps={{ "data-testid": "person-country-select" }}
                                            >
                                                {Object.entries(countries).map(([key, label]) => (
                                                    <MenuItem value={key} key={key} data-testid={`person-country-${key}`}>{label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField margin="normal" required fullWidth id="contact-gsm" label={t('gsm')} name="gsm" value={personalInfo.gsm} onChange={handleChange}
                                                   error={!!errors.gsm}
                                                   helperText={errors.gsm}
                                                   disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-gsm" }}/>
                                        <TextField required margin="normal" fullWidth id="contact-email" label={t('email')} name="email" value={personalInfo.email}
                                                   onChange={handleChange}
                                                   error={!!errors.email} helperText={errors.email} disabled={readOnly}
                                                   inputProps={{ "data-testid": "person-email" }}/>

                                    </Box>
                                </CardContent>
                                <CardActions style={{
                                    display: 'flex', justifyContent: location.pathname !== "/mijn-profiel" ? 'space-between' : 'right'
                                }}>
                                    {location.pathname !== "/mijn-profiel" && (
                                        <>
                                            <Button onClick={goBack} variant="contained" disabled={isLoading} data-testid="previous-button">
                                                {t('previous')}
                                            </Button>
                                            <Button type="submit" variant="contained" disabled={isLoading} data-testid="next-button">
                                                {t('next')}
                                            </Button>
                                        </>
                                    )}
                                    {!readOnly && user.email && location.pathname === "/mijn-profiel" && (
                                        <Button onClick={updatePerson} variant="contained" disabled={isLoading} data-testid="update-button">
                                            {t('update')}
                                        </Button>
                                    )}
                                </CardActions>
                            </Card>
                        </Box>
                    </Grid>
                    <AppSnackbar openSnackbar={openSnackbar} snackbarMessage={snackbarMessage} setOpenSnackbar={setOpenSnackbar}/>
                </Grid>)}
        </>
    )
};

export default withAuthenticationRequired(PersonForm, {
    onRedirecting: () => <Loading/>,
    returnTo: '/nieuwe-reservatie'
});
